<script lang="ts">
    import { onMount, getContext } from 'svelte';
    import { getBaseBuildPeakPcnt, getCurrentFitnessSignature, getCurrentTrainingLoads, projectUserFitness, type XSSDetails } from '@/Util/utils';
    import { 
        type ProgramFocusTypes, 
        defaultBbpPlan,
        type BbpPlan,
        type XataFormProps,
        ProgramType,
        specificityValues,
    } from "@/Common/constants";
    import { DatePicker} from "@/Common";
    import type { MyFitnessStores } from '@/MyFitness/stores';
    import { createUserFocusPowers } from './modules/UserFocusPowers';
    import { LocalDate } from '@/time';
    import { writable } from 'svelte/store';
    import { FocusSlider, SpecificitySlider } from './ProgramElements/SetupFormInputs';
    import XataGeneralSettings from './ProgramElements/XataGeneralSettings.svelte';
    import { differenceInDays, subDays } from 'date-fns';
    import { ProgramAchievabilityMessage, ProgramFitnessSignature, ProgramTrainingLoad } from './ProgramElements';
    import { type AchievabilityStatus, createProgramAchievability } from './modules/ProgramAchievability';
    import TrainPrefDayPicker from './ProgramElements/TrainPrefDayPicker.svelte';

    const { userParams, trainingAdvice } = getContext<MyFitnessStores>('myFitness')
    let currentSignature: XSSDetails
    let currentTL: XSSDetails

    function reactiveUpdate() {
        currentSignature = getCurrentFitnessSignature($trainingAdvice)
        currentTL = getCurrentTrainingLoads($trainingAdvice)
        focusPower.updateFocusPower($plan.targetAthlete)
        if(projectedFS) setProjectedFitness() // Ensures it doesn't run on first load
    }
    // $: if($trainingAdvice) reactiveUpdate()
    $: if($trainingAdvice && !disabled) reactiveUpdate()
    
    let athleteTypes = Object.keys($userParams.athletes) as ProgramFocusTypes[]
    
    type $$Props = XataFormProps<BbpPlan>
    export let plan = writable({...defaultBbpPlan})
    let className = ""
    export {className as class}
    export let disabled = false
    export let achievabilityId = "challenge-achievability"
    export let achievability: AchievabilityStatus

    $: achievability = $overallAchievability

    /* Date input targetDate */
    let targetDate: string

    /* Current Focus Power and Duration */
    let focusPower = createUserFocusPowers(trainingAdvice)
    const { focusPowerDuration, currentFocusPower } = focusPower

    /* Achievability */
    const programAchievability = createProgramAchievability(ProgramType.BaseBuildPeak)
    const {targetAchievability, overallAchievability, statusMessage} = programAchievability

    /* Projected FS, TL and Focus */
    let projectedFS: XSSDetails
    let projectedTL: XSSDetails
    let projectedFocusPower: number
    
    /* For the BBP Phases Bar */
    let slider_bgPcnt: ReturnType<typeof getBaseBuildPeakPcnt> = null
    let slider_styleString = 'background: #9B9B9B;'
    let startDatePcnt: number = null
    let phase = 'Base'

    async function setProjectedFitness() {
        const projectedUserFitness = await projectUserFitness(
            currentSignature, currentTL, $userParams.ir_params, $plan.irValue, 
            specificityValues[$plan.specificity], $userParams.athletes[$plan.targetAthlete].duration, 
            differenceInDays(new Date(), subDays($plan.targetDate, 120)), 120, 
            ProgramType.BaseBuildPeak, $plan.settings.difficulty
        )
        projectedFS = projectedUserFitness.sig
        projectedTL = projectedUserFitness.tl
        projectedFocusPower = projectedUserFitness.fp
        programAchievability.validateRampRates($plan.irValue, currentTL)
    }

    const updateBBPbar = (targetDate: Date) => {
        // Right now we have the target date restricted so it can't be made > 120 days, but I'm keeping this logic in case we change that
        const planLength = differenceInDays(targetDate, new Date())
        const barSize = Math.max(130, planLength)
        slider_bgPcnt = getBaseBuildPeakPcnt(0, barSize)
        slider_styleString = `background: linear-gradient(to right, #9B9B9B ${slider_bgPcnt.base}%, #4A90E2 ${slider_bgPcnt.base}%, #4A90E2 ${slider_bgPcnt.build}%, #7ED321 ${slider_bgPcnt.build}%, #7ED321 ${slider_bgPcnt.peak}%, #F5A623 ${slider_bgPcnt.peak}%)`
        startDatePcnt = (barSize - planLength) / barSize * 100

        if (startDatePcnt < slider_bgPcnt.base) {
            phase = 'Pre-Base'
        } else if (startDatePcnt < slider_bgPcnt.build) {
            phase = 'Base'
        } else if (startDatePcnt < slider_bgPcnt.peak) {
            phase = 'Build'
        } else {
            phase = 'Peak'
        }
    }
    $: updateBBPbar($plan.targetDate)

    onMount(() => {
        focusPower.updateFocusPower($plan.targetAthlete)
        targetDate = LocalDate.format($plan.targetDate) 
        setProjectedFitness()
    })
</script>
<div class={className}>
    <div class="flex flex-col gap-5 flex-1 md:flex-row">
        <div class="basis-2/5 grid grid-cols-[2fr,5fr] auto-rows-max items-start gap-y-8 gap-x-3 md:gap-x-6 md:text-right text-left text-base md:grid">

            <span class="text-slate-600">Date</span>
            <DatePicker class="max-w-[13rem]" min={LocalDate.format(new Date())} max={LocalDate.format(defaultBbpPlan.targetDate)} {disabled}
                bind:value={targetDate}
                on:input={ () => {
                    $plan.targetDate = LocalDate.atNoon(targetDate)
                    setProjectedFitness()
                }}
            />

            <span class="text-slate-600">Focus</span>
            <FocusSlider {athleteTypes} {disabled}
                bind:targetAthlete={$plan.targetAthlete}
                onInput={() => {
                    focusPower.updateFocusPower($plan.targetAthlete)
                    setProjectedFitness()
                }}
                focusPowerDuration={$focusPowerDuration}
            />

            <span class="text-slate-600">Specificity</span>
            <SpecificitySlider {disabled}
                bind:specificity={$plan.specificity}
                on:change={() => {
                    setProjectedFitness()
                }}
            />

            <span class="text-slate-600">Improvement Rate</span>
            <select class="input-xfai" {disabled}
                bind:value={$plan.irValue}
                on:change={() => {
                    setProjectedFitness()
                }}
            >
                {#each $userParams.training_gradients as ir}
                        <option value="{ir['value']}">{ir['text']}</option>
                {/each}
            </select>

            <TrainPrefDayPicker class='col-span-2' bind:cal_settings={$plan.cal_settings} />

            <XataGeneralSettings class="col-span-2" {disabled} {plan} />
            
        </div>

        <div class="h-px md:w-px md:h-auto bg-black/10"/>

        <div class="basis-3/5 flex flex-col gap-10 text-base">
            {#if slider_bgPcnt && startDatePcnt}
                <div class="flex flex-col gap-3 text-left">
                    <span class="font-bold">
                        Program Phase
                    </span>
                    <div class="slider-container relative mb-9">
                        <div class="select-slider my-3 min-h-[11px]" style={slider_styleString} />
        
                        <span style={`left: ${(slider_bgPcnt.build + slider_bgPcnt.base)/2}%;`}>Base</span>
                        <span style={`left: ${(slider_bgPcnt.peak + slider_bgPcnt.build)/2}%;`}>Build</span>
                        <span style={`left: ${(100 + slider_bgPcnt.peak)/2}%;`}>Peak</span>

                        <span class="top-[calc(100%+25px)] text-[#34495e] w-max" style="left: {startDatePcnt}%">
                            Plan Start

                            <i class="fa-solid fa-triangle"/>
                        </span>
                    </div>
                    <span>Your plan will begin in the <strong>{phase}</strong> phase of the program</span>
                </div>
            {/if}

            <div id={achievabilityId} class="flex flex-col gap-3 text-left">
                <span class="font-bold">
                    Achievability
                </span>
                <ProgramAchievabilityMessage statusMessage={$statusMessage} status={$overallAchievability}/>
            </div>

            <ProgramFitnessSignature 
                {currentSignature} targetAchievability={$targetAchievability} targetSignature={projectedFS}
                estimatedRowName="Projected"
            />
            
            <ProgramTrainingLoad 
                {currentTL} targetAchievability={$targetAchievability} targetTL={projectedTL}
                estimatedRowName="Projected"
            />
        </div>
    </div>
</div>

<style lang='scss'>
    .slider-container {
        max-width: 100%;
        width: 100%;

        span {
            position: absolute;
            top: 50%;
            transform: translate(-50%, -50%);
            font-weight: bold;
            color: white;
            pointer-events: none;

            i {
                position: absolute;
                bottom: 100%;
                left: 50%;
                transform: translate(-50%, 0);
                color: inherit;
            }
        }
        .select-slider {
            -webkit-appearance: none;
            appearance: none;
            height: 30px;
            outline: none;
            border-radius: 4px !important;
            position: relative;
        }
    }
</style>